import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface ContratoData {
	tipoCaucion: string
	polizaElectronica: string
	sumaAsegurada: string
	montoContrato: string
	numeroLicitacion: string
	objetoContrato: string
	vigenciaEstimada: string
	certificacionFirmas: string
}

interface CaucionGeneralContratoScreenProps {
	onNext: (data: ContratoData) => void
	onBack: () => void
	data?: Partial<ContratoData>
}

const TIPO_CAUCION_OPTIONS = [
	{ value: 'gtia-contrato', label: 'Garantía de Contrato de Ejecución' },
	{ value: 'gtia-servicio', label: 'Garantía de Servicio' },
	{ value: 'gtia-suministro', label: 'Contrato de Suministro' },
	{
		value: 'gtia-contrataciones',
		label: 'Garantía Contrataciones y Concesiones',
	},
	{ value: 'gtia-judiciales', label: 'Garantía Judiciales' },
]

export function CaucionGeneralContratoScreen({
	onNext,
	onBack,
	data,
}: CaucionGeneralContratoScreenProps) {
	const [formData, setFormData] = useState<ContratoData>({
		tipoCaucion: data?.tipoCaucion || '',
		polizaElectronica: data?.polizaElectronica || '',
		sumaAsegurada: data?.sumaAsegurada || '',
		montoContrato: data?.montoContrato || '',
		numeroLicitacion: data?.numeroLicitacion || '',
		objetoContrato: data?.objetoContrato || '',
		vigenciaEstimada: data?.vigenciaEstimada || '',
		certificacionFirmas: data?.certificacionFirmas || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const formatCurrency = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (!cleaned) return ''
		return new Intl.NumberFormat('es-AR').format(parseInt(cleaned, 10))
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.tipoCaucion) {
			newErrors.tipoCaucion = 'Selecciona el tipo de caución'
		}
		if (!formData.polizaElectronica) {
			newErrors.polizaElectronica = 'Indica si requiere póliza electrónica'
		}
		if (!formData.sumaAsegurada.trim()) {
			newErrors.sumaAsegurada = 'Ingresa la suma asegurada'
		}
		if (!formData.montoContrato.trim()) {
			newErrors.montoContrato = 'Ingresa el monto del contrato'
		}
		if (!formData.numeroLicitacion.trim()) {
			newErrors.numeroLicitacion = 'Ingresa el número de licitación/expediente'
		}
		if (!formData.objetoContrato.trim()) {
			newErrors.objetoContrato = 'Ingresa el objeto del contrato'
		}
		if (!formData.vigenciaEstimada.trim()) {
			newErrors.vigenciaEstimada = 'Ingresa la vigencia estimada'
		}
		if (!formData.certificacionFirmas) {
			newErrors.certificacionFirmas = 'Indica si requiere certificación'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.tipoCaucion &&
		formData.polizaElectronica &&
		formData.sumaAsegurada.trim() &&
		formData.montoContrato.trim() &&
		formData.numeroLicitacion.trim() &&
		formData.objetoContrato.trim() &&
		formData.vigenciaEstimada.trim() &&
		formData.certificacionFirmas

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Contrato
					</h2>

					<div className="flex w-full flex-col gap-4">
						<div className="flex flex-col gap-1">
							<label className="text-sm font-medium text-neutral-700">
								Tipo de Seguro de Caución{' '}
								<span className="text-red-500">*</span>
							</label>
							<select
								className={`w-full rounded-xl border px-4 py-3 text-base ${
									errors.tipoCaucion ? 'border-red-500' : 'border-neutral-300'
								}`}
								value={formData.tipoCaucion}
								onChange={(e) => {
									setFormData({ ...formData, tipoCaucion: e.target.value })
									setErrors({ ...errors, tipoCaucion: '' })
								}}
							>
								<option value="">Selecciona el tipo</option>
								{TIPO_CAUCION_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
							{errors.tipoCaucion && (
								<span className="text-sm text-red-500">
									{errors.tipoCaucion}
								</span>
							)}
						</div>

						<div className="flex flex-col gap-1">
							<label className="text-sm font-medium text-neutral-700">
								Póliza Electrónica (CONTRAT.AR/COMPRAR.AR){' '}
								<span className="text-red-500">*</span>
							</label>
							<div className="flex gap-4">
								<label className="flex items-center gap-2">
									<input
										type="radio"
										name="polizaElectronica"
										value="si"
										checked={formData.polizaElectronica === 'si'}
										onChange={(e) => {
											setFormData({
												...formData,
												polizaElectronica: e.target.value,
											})
											setErrors({ ...errors, polizaElectronica: '' })
										}}
										className="size-4"
									/>
									<span>Sí</span>
								</label>
								<label className="flex items-center gap-2">
									<input
										type="radio"
										name="polizaElectronica"
										value="no"
										checked={formData.polizaElectronica === 'no'}
										onChange={(e) => {
											setFormData({
												...formData,
												polizaElectronica: e.target.value,
											})
											setErrors({ ...errors, polizaElectronica: '' })
										}}
										className="size-4"
									/>
									<span>No</span>
								</label>
							</div>
							{errors.polizaElectronica && (
								<span className="text-sm text-red-500">
									{errors.polizaElectronica}
								</span>
							)}
						</div>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Suma Asegurada ($)"
								labelPosition="internal"
								size="l"
								placeholder="Monto"
								value={formData.sumaAsegurada}
								onChange={(e) => {
									setFormData({
										...formData,
										sumaAsegurada: formatCurrency(e.target.value),
									})
									setErrors({ ...errors, sumaAsegurada: '' })
								}}
								errorText={errors.sumaAsegurada}
								required
							/>
							<InputText
								label="Monto Total Contrato ($)"
								labelPosition="internal"
								size="l"
								placeholder="Monto"
								value={formData.montoContrato}
								onChange={(e) => {
									setFormData({
										...formData,
										montoContrato: formatCurrency(e.target.value),
									})
									setErrors({ ...errors, montoContrato: '' })
								}}
								errorText={errors.montoContrato}
								required
							/>
						</div>

						<InputText
							label="N° Licitación / Expediente / Contrato"
							labelPosition="internal"
							size="l"
							placeholder="Número de referencia"
							value={formData.numeroLicitacion}
							onChange={(e) => {
								setFormData({ ...formData, numeroLicitacion: e.target.value })
								setErrors({ ...errors, numeroLicitacion: '' })
							}}
							errorText={errors.numeroLicitacion}
							required
						/>

						<div className="flex flex-col gap-1">
							<label className="text-sm font-medium text-neutral-700">
								Objeto de la Licitación o Contrato{' '}
								<span className="text-red-500">*</span>
							</label>
							<textarea
								className={`w-full rounded-xl border px-4 py-3 text-base ${
									errors.objetoContrato
										? 'border-red-500'
										: 'border-neutral-300'
								}`}
								rows={3}
								placeholder="Descripción del objeto"
								value={formData.objetoContrato}
								onChange={(e) => {
									setFormData({ ...formData, objetoContrato: e.target.value })
									setErrors({ ...errors, objetoContrato: '' })
								}}
							/>
							{errors.objetoContrato && (
								<span className="text-sm text-red-500">
									{errors.objetoContrato}
								</span>
							)}
						</div>

						<InputText
							label="Vigencia Estimada (meses o días)"
							labelPosition="internal"
							size="l"
							placeholder="Ej: 12 meses"
							value={formData.vigenciaEstimada}
							onChange={(e) => {
								setFormData({ ...formData, vigenciaEstimada: e.target.value })
								setErrors({ ...errors, vigenciaEstimada: '' })
							}}
							errorText={errors.vigenciaEstimada}
							required
						/>

						<div className="flex flex-col gap-1">
							<label className="text-sm font-medium text-neutral-700">
								Certificación de Firmas por Escribano y/o Colegio{' '}
								<span className="text-red-500">*</span>
							</label>
							<div className="flex gap-4">
								<label className="flex items-center gap-2">
									<input
										type="radio"
										name="certificacionFirmas"
										value="si"
										checked={formData.certificacionFirmas === 'si'}
										onChange={(e) => {
											setFormData({
												...formData,
												certificacionFirmas: e.target.value,
											})
											setErrors({ ...errors, certificacionFirmas: '' })
										}}
										className="size-4"
									/>
									<span>Sí</span>
								</label>
								<label className="flex items-center gap-2">
									<input
										type="radio"
										name="certificacionFirmas"
										value="no"
										checked={formData.certificacionFirmas === 'no'}
										onChange={(e) => {
											setFormData({
												...formData,
												certificacionFirmas: e.target.value,
											})
											setErrors({ ...errors, certificacionFirmas: '' })
										}}
										className="size-4"
									/>
									<span>No</span>
								</label>
							</div>
							{errors.certificacionFirmas && (
								<span className="text-sm text-red-500">
									{errors.certificacionFirmas}
								</span>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
